import { useState, useEffect } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell, CartesianGrid, Legend } from "recharts";
import { Users, Clock, GitBranch, Layers, UserPlus, Building2, Crown, LogOut } from "lucide-react";

const MONDAY = ["#6161FF", "#00C875", "#FDAB3D", "#A25DDC", "#579BFC", "#E2445C", "#66CCFF", "#FF7575"];

const Kpi = ({ icon: Icon, label, value, suffix, accent, testid }) => (
  <div className="p-4 rounded-xl bg-white border border-[#181B34]/10 shadow-sm" style={{ borderTop: `4px solid ${accent}` }} data-testid={testid}>
    <div className="flex items-center gap-2 text-[#9699AD]"><Icon size={14} /><span className="text-[10px] font-bold uppercase tracking-[0.15em]">{label}</span></div>
    <p className="mt-1.5 text-3xl font-black">{value}<span className="text-base font-bold text-[#9699AD] ml-1">{suffix}</span></p>
  </div>
);

const Card = ({ title, subtitle, children, testid, className = "" }) => (
  <div className={`p-5 rounded-2xl bg-white border border-[#181B34]/10 shadow-sm ${className}`} data-testid={testid}>
    <h3 className="font-black">{title}</h3>
    {subtitle && <p className="text-xs text-[#676a82] font-medium mt-0.5">{subtitle}</p>}
    <div className="mt-4">{children}</div>
  </div>
);

const tipStyle = { borderRadius: 12, border: "1px solid rgba(24,27,52,0.1)", fontWeight: 600, fontSize: 12 };

export default function AnalyticsPage() {
  const [d, setD] = useState(null);
  const [views, setViews] = useState([]);
  const [chartId, setChartId] = useState("");
  useEffect(() => { api.get("/views").then((r) => setViews(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    setD(null);
    api.get(`/analytics${chartId ? `?chart_id=${chartId}` : ""}`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [chartId]);
  if (!d) return <div className="h-full flex items-center justify-center"><div className="w-8 h-8 rounded-full border-4 border-[#6161FF] border-t-transparent animate-spin" /></div>;

  return (
    <div className="h-full overflow-auto p-8" data-testid="analytics-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-black tracking-tight">Org Analytics</h1>
            <p className="text-[#676a82] font-medium mt-1">A living picture of your organization's shape, computed from the directory in real time.</p>
          </div>
          <select data-testid="analytics-chart-filter" value={chartId} onChange={(e) => setChartId(e.target.value)}
            className="h-11 px-3 rounded-xl bg-white border border-[#181B34]/10 text-sm font-bold min-w-56">
            <option value="">All charts</option>
            {views.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </div>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi icon={Users} label="Headcount" value={d.kpis.headcount} accent="#6161FF" testid="kpi-headcount" />
          <Kpi icon={Clock} label="Avg tenure" value={d.kpis.avg_tenure} suffix="yrs" accent="#00C875" testid="kpi-avg-tenure" />
          <Kpi icon={GitBranch} label="Avg span of control" value={d.kpis.avg_span} suffix="reports" accent="#FDAB3D" testid="kpi-avg-span" />
          <Kpi icon={Layers} label="Org depth" value={d.kpis.org_depth} suffix="levels" accent="#A25DDC" testid="kpi-org-depth" />
          <Kpi icon={Crown} label="People managers" value={d.kpis.managers} accent="#579BFC" testid="kpi-managers" />
          <Kpi icon={Building2} label="Departments" value={d.kpis.departments} accent="#6161FF" testid="kpi-departments" />
          <Kpi icon={UserPlus} label="New hires (90d)" value={d.kpis.new_hires_90d} accent="#00C875" testid="kpi-new-hires" />
          <Kpi icon={LogOut} label="Exited" value={d.kpis.exited} accent="#E2445C" testid="kpi-exited" />
        </div>

        <div className="mt-6 grid lg:grid-cols-2 gap-6">
          <Card title="Tenure mix by department" subtitle="Who's new, who's seasoned — per team" testid="chart-tenure-dept">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={d.tenure_by_dept} layout="vertical" margin={{ left: 12 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eceef7" />
                <XAxis type="number" tick={{ fontSize: 11, fontWeight: 600 }} allowDecimals={false} />
                <YAxis type="category" dataKey="department" width={110} tick={{ fontSize: 11, fontWeight: 700 }} />
                <Tooltip contentStyle={tipStyle} />
                <Legend wrapperStyle={{ fontSize: 11, fontWeight: 700 }} />
                {d.band_labels.map((b) => (
                  <Bar key={b} dataKey={b} stackId="t" fill={d.band_colors[b]} radius={[0, 0, 0, 0]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Card title="Headcount growth" subtitle="Cumulative joins over the last 24 months (active employees)" testid="chart-headcount-trend">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={d.headcount_trend}>
                <defs>
                  <linearGradient id="hc" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6161FF" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#6161FF" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eceef7" />
                <XAxis dataKey="month" tick={{ fontSize: 10, fontWeight: 600 }} interval={3} />
                <YAxis tick={{ fontSize: 11, fontWeight: 600 }} allowDecimals={false} />
                <Tooltip contentStyle={tipStyle} />
                <Area type="monotone" dataKey="headcount" stroke="#6161FF" strokeWidth={2.5} fill="url(#hc)" />
              </AreaChart>
            </ResponsiveContainer>
          </Card>

          <Card title="Span of control" subtitle="How many direct reports managers carry" testid="chart-span">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={d.span_buckets}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eceef7" />
                <XAxis dataKey="bucket" tick={{ fontSize: 12, fontWeight: 700 }} />
                <YAxis tick={{ fontSize: 11, fontWeight: 600 }} allowDecimals={false} />
                <Tooltip contentStyle={tipStyle} />
                <Bar dataKey="managers" fill="#FDAB3D" radius={[8, 8, 0, 0]} maxBarSize={64} />
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-3 border-t border-[#181B34]/8 pt-3">
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#9699AD] mb-2">Widest spans</p>
              {d.top_managers.slice(0, 5).map((m) => (
                <div key={m.name} className="flex items-center gap-2 py-1 text-sm">
                  <span className="font-bold">{m.name}</span>
                  <span className="text-xs text-[#676a82] font-medium truncate">{m.designation}</span>
                  <span className="ml-auto text-xs font-black px-2 py-0.5 rounded-full bg-[#6161FF]/10 text-[#6161FF]">{m.reports} reports</span>
                </div>
              ))}
            </div>
          </Card>

          <div className="grid grid-rows-2 gap-6">
            <Card title="Employment type" testid="chart-etype">
              <div className="flex items-center gap-4">
                <ResponsiveContainer width="55%" height={150}>
                  <PieChart>
                    <Pie data={d.employment_type} dataKey="value" nameKey="name" innerRadius={42} outerRadius={68} paddingAngle={3}>
                      {d.employment_type.map((_, i) => <Cell key={i} fill={MONDAY[i % MONDAY.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={tipStyle} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-1.5">
                  {d.employment_type.map((t, i) => (
                    <div key={t.name} className="flex items-center gap-2 text-sm font-semibold">
                      <span className="w-3 h-3 rounded" style={{ background: MONDAY[i % MONDAY.length] }} />
                      {t.name} <span className="font-black">{t.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
            <Card title="Locations" testid="chart-locations">
              <div className="space-y-2">
                {d.locations.slice(0, 6).map((l, i) => (
                  <div key={l.name} className="flex items-center gap-3">
                    <span className="text-sm font-bold w-24 truncate">{l.name}</span>
                    <div className="flex-1 h-5 rounded-full bg-[#F0F3FF] overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${(l.value / d.kpis.headcount) * 100}%`, background: MONDAY[i % MONDAY.length] }} />
                    </div>
                    <span className="text-sm font-black w-7 text-right">{l.value}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
